import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import type { RootState } from '@src/store/store';
import { logout, setNewToken } from '@src/store/user/userSlice';
import { Result } from './api.types';
import { message } from 'antd';
import { authConfig } from '@src/auth/config';
import { setCookie } from '@src/utilities/cookie';
import { COOKIES } from '@src/constants/cookies';

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = (getState as () => RootState)();
    const token = state.user.user?.accessToken;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  },
});

const baseQueryWithReauth = async (
  args: any,
  api: any,
  extraOptions: any
): Promise<Result> => {
  await mutex.waitForUnlock();

  let result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const state = api.getState() as RootState;
        const refreshToken = state.user.user?.refreshToken;

        if (refreshToken) {
          const refreshResult = await baseQuery(
            {
              url: `${authConfig.domain}/oauth/token`,
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: {
                grant_type: 'refresh_token',
                client_id: authConfig.clientId,
                refresh_token: refreshToken,
              },
            },
            api,
            extraOptions
          );

          if (refreshResult.data) {
            const { access_token, refresh_token, id_token, expires_in } =
              refreshResult.data as any;

            api.dispatch(
              setNewToken({
                accessToken: access_token,
                refreshToken: refresh_token,
                idToken: id_token,
                expiresIn: expires_in,
              })
            );
            result = await baseQuery(args, api, extraOptions);
          } else {
            api.dispatch(logout(undefined));
          }
        } else {
          api.dispatch(logout(undefined));
          setCookie(COOKIES.LOGGED_IN.NAME, '', 0);
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  if (result.error?.status === 500) {
    message.open({
      type: 'error',
      content:
        'Our server is having trouble right now. Please call our support team for assistance.',
      className: 'response-error',
    });
  }
  return result as Result;
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [],
  endpoints: () => ({}),
});
